import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { SharedService } from '@app/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { QtySyncDebouncer } from '../../helpers/qty-sync-debouncer';
import { CartUpdateEvent, SupplierProduct, SupplierSection } from '../../models/supplier.models';
import { UdaanCartProduct } from '../../models/udaan-cart';
import { PriceDiscoveryService } from '../../services/price-discovery.service';

/**
 * Child component that encapsulates Udaan-specific logic:
 * - Cart fetch / local sync
 * - Add to cart / update quantity
 * - Proceed-to-buy flow
 *
 * Follows the same pattern as HyperpureSupplierComponent for consistency.
 */
@Component({
    selector: 'app-udaan-supplier',
    template: '', // Udaan has no standalone template; all UI is in the parent
    styles: []
})
export class UdaanSupplierComponent implements OnChanges, OnDestroy {

    // ── Inputs from parent ──
    @Input() entityId = '';
    @Input() storeUuid = '';           // Internal store ID (MD5 hash) - used for update cart, etc.
    @Input() apiStoreId = '';          // Actual Udaan store ID from udaanResponse.stores[0].storeId - used for get cart API only
    @Input() supplierSection: SupplierSection | null = null;

    // ── Cart State ──
    apiCartItems: SupplierProduct[] = [];
    apiCartProducts: UdaanCartProduct[] = []; // Raw cart data from API
    apiCartCount = 0;
    cartQuantityMap = new Map<string, number>();

    isCartLoading = false;
    private cartFetched = false; // Track if cart was already fetched

    // ── Outputs to parent ──
    @Output() cartUpdated = new EventEmitter<CartUpdateEvent>();
    @Output() checkoutComplete = new EventEmitter<void>();

    private destroy$ = new Subject<void>();
    private qtyDebouncer = new QtySyncDebouncer(600);
    private cartRequestCount = 0;

    get isCartSyncing(): boolean {
        return this.cartRequestCount > 0 || this.qtyDebouncer.hasPending();
    }

    constructor(
        private priceDiscoveryService: PriceDiscoveryService,
        private sharedService: SharedService
    ) { }

    /**
     * Lifecycle hook - automatically fetch cart when inputs change
     */
    ngOnChanges(changes: SimpleChanges): void {
        // Auto-fetch cart when apiStoreId is set for the first time
        if (changes['apiStoreId'] && this.apiStoreId && this.entityId && !this.cartFetched) {
            console.log('📦 [Udaan] apiStoreId changed, auto-fetching cart...');
            this.fetchCart();
        }
    }

    ngOnDestroy(): void {
        this.qtyDebouncer.clearAll();
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Called by parent after entity changes.
    */
    resetState(): void {
        this.qtyDebouncer.clearAll();
        this.apiCartItems = [];
        this.apiCartProducts = [];
        this.apiCartCount = 0;
        this.cartQuantityMap.clear();
        this.isCartLoading = false;
        this.cartFetched = false;
    }

    /**
     * Initializes the component - called by parent after entity/store is set.
     */
    initialize(): void {
        if (this.storeUuid && this.entityId) {
            this.fetchCart();
        }
    }

    /**
     * Fetches the current Udaan cart and populates the quantity map.
     * Response format: { products: UdaanCartProduct[], cart_count: number }
     * Uses apiStoreId (actual Udaan store ID) for get cart API if available
     */
    fetchCart(): Promise<boolean> {
        if (!this.entityId || !this.storeUuid) return Promise.resolve(false);

        this.isCartLoading = true;
        return new Promise((resolve) => {

        // Use apiStoreId for get cart API if available, otherwise fall back to storeUuid
        const storeIdForCart = this.apiStoreId || this.storeUuid;
        console.log('📦 [Udaan] Fetching cart with store ID:', storeIdForCart);

        this.priceDiscoveryService.getUdaanCart(storeIdForCart, this.entityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    console.log('📦 [Udaan] Raw response:', response);

                    // Handle potentially wrapped response (some APIs wrap in {response: {products: []}})
                    const data = response?.response || response;

                    // Udaan API returns: { products: [...], cartCount: number }
                    const products: any[] = data?.products || [];

                    // Use cartCount from API response (handles both camelCase and snake_case)
                    const cartCountFromApi = data?.cartCount ?? data?.cart_count;

                    console.log('📦 [Udaan] Extracted products count:', products.length);
                    console.log('📦 [Udaan] Cart count from API:', cartCountFromApi);
                    if (products.length > 0) {
                        console.log('📦 [Udaan] First product raw:', products[0]);
                    }

                    this.apiCartProducts = products;
                    this.apiCartCount = cartCountFromApi ?? 0; // Use cartCount from API response

                    // Build quantity map for quick lookup
                    // Note: API returns snake_case but FE converts to camelCase
                    this.cartQuantityMap.clear();
                    products.forEach((product: any) => {
                        const productId = product.productId || product.product_id;
                        const productQty = product.productQuantity || product.product_quantity;
                        if (productId) {
                            this.cartQuantityMap.set(
                                String(productId),
                                Number(productQty) || 0
                            );
                        }
                    });

                    // Normalize to SupplierProduct interface (same as Hyperpure pattern)
                    // Note: API returns snake_case but FE converts to camelCase
                    this.apiCartItems = products.map((product: any) => {
                        const mappedItem = {
                            item_name: product.productName || product.product_name || '',
                            sku_id: String(product.productId || product.product_id || ''),
                            rate: Number(product.discountedPricePerUnit || product.discounted_price_per_unit || product.pricePerUnit || product.price_per_unit || 0),
                            mrp: Number(product.pricePerUnit || product.price_per_unit || 0),
                            price: Number(product.discountedPricePerUnit || product.discounted_price_per_unit || product.pricePerUnit || product.price_per_unit || 0),
                            unit: 'unit', // Udaan doesn't provide UOM in cart response
                            quantity: product.productQuantity || product.product_quantity || 1,
                            platform: 'udaan',
                            isBestMatch: false,
                            is_in_stock: (product.availableOrderQty || product.available_order_qty) !== 0,
                            avlOrderQty: product.availableOrderQty || product.available_order_qty,
                            selectedQuantity: product.productQuantity || product.product_quantity || 1,
                            product_number: product.productNumber || product.product_number || '',
                            category_id: product.categoryId || product.category_id || '',
                            vertical: product.vertical || ''
                        } as SupplierProduct;
                        return mappedItem;
                    });

                    console.log('📦 [Udaan] Mapped apiCartItems count:', this.apiCartItems.length);
                    if (this.apiCartItems.length > 0) {
                        console.log('📦 [Udaan] First mapped item:', this.apiCartItems[0]);
                    }

                    // Update supplierSection items with quantities from API cart
                    if (this.supplierSection) {
                        this.supplierSection.items.forEach(item => {
                            const skuId = String(item.sku_id || '');
                            if (this.qtyDebouncer.isPending(skuId)) {
                                return;
                            }
                            if (this.cartQuantityMap.has(skuId)) {
                                item.selectedQuantity = this.cartQuantityMap.get(skuId) || 0;
                            }
                        });
                    }

                    // Save to localStorage for persistence
                    try {
                        const cartData = products.map((p: any) => ({
                            product_id: p.productId || p.product_id,
                            product_name: p.productName || p.product_name,
                            quantity: p.productQuantity || p.product_quantity,
                            price: p.discountedPricePerUnit || p.discounted_price_per_unit || p.pricePerUnit || p.price_per_unit
                        }));
                        localStorage.setItem('udaanCart', JSON.stringify(cartData));
                    } catch (error) {
                        console.error('Failed to save Udaan cart to localStorage:', error);
                    }

                    // Emit cart update event to parent
                    this.cartUpdated.emit({
                        platform: 'udaan',
                        cartCount: this.apiCartCount,
                        cartItems: this.apiCartItems
                    });

                    this.isCartLoading = false;
                    this.cartFetched = true;
                    console.log(`✅ [Udaan] Cart fetched successfully. Items: ${this.apiCartCount}`);
                    resolve(true);
                },
                error: (err) => {
                    console.error('❌ Failed to fetch Udaan cart', err);
                    this.isCartLoading = false;
                    // Don't show error to user - cart fetch failures shouldn't block the UI
                    resolve(false);
                }
                    });
        });
    }

    /**
     * Called when an item is first added to the cart
     */
    addToCart(item: SupplierProduct, supplierName: string, previousQuantity: number): void {
        this.qtyDebouncer.schedule(String(item.sku_id), previousQuantity, (baseline) => {
            this.updateUdaanCartSync(item, item.selectedQuantity || 1, baseline);
        });
        this.syncCartLocally(item);
    }

    /**
     * Called when an item's quantity is updated (incremented/decremented)
     */
    updateQuantity(item: SupplierProduct, change: number, previousQuantity: number): void {
        this.qtyDebouncer.schedule(String(item.sku_id), previousQuantity, (baseline) => {
            this.updateUdaanCartSync(item, item.selectedQuantity || 0, baseline);
        });
        this.syncCartLocally(item);
    }

    flushPendingQuantitySyncs(): void {
        this.qtyDebouncer.flushAll();
    }

    /** Drops queued quantity edits without sending them. Used before clearing the cart. */
    cancelPendingQuantitySyncs(): void {
        this.qtyDebouncer.clearAll();
    }

    /** Hides the local cart immediately. The next fetchCart replaces it with the server cart. */
    stageClearedCart(): void {
        this.apiCartItems = [];
        this.apiCartCount = 0;
        this.cartQuantityMap.clear();
        this.cartUpdated.emit({
            platform: 'udaan',
            cartCount: 0,
            cartItems: []
        });
    }

    /**
     * Removes one line immediately. Does not refresh the cart; the parent GETs once
     * after every removal in the batch has settled.
     */
    removeLineNow(item: SupplierProduct): Promise<void> {
        const skuId = String(item.sku_id);
        const known = this.supplierSection?.items.find((entry) => String(entry.sku_id) === skuId);
        return new Promise((resolve, reject) => {
            this.priceDiscoveryService.updateUdaanCart(
                String(item.sku_id),
                0,
                'Delivery',
                this.storeUuid,
                item.category_id || known?.category_id || '',
                this.entityId,
                item.vertical || known?.vertical || '',
                true
            ).pipe(takeUntil(this.destroy$)).subscribe({
                next: () => resolve(),
                error: (error) => reject(error),
            });
        });
    }

    private syncCartLocally(item: SupplierProduct): void {
        const skuId = String(item.sku_id);
        const qty = item.selectedQuantity || 0;
        if (qty > 0) {
            this.cartQuantityMap.set(skuId, qty);
            const existing = this.apiCartItems.find(i => String(i.sku_id) === skuId);
            if (existing) {
                existing.selectedQuantity = qty;
            } else {
                this.apiCartItems = [...this.apiCartItems, { ...item }];
            }
        } else {
            this.cartQuantityMap.delete(skuId);
            this.apiCartItems = this.apiCartItems.filter(i => String(i.sku_id) !== skuId);
        }
        this.apiCartCount = this.apiCartItems.length;
        this.cartUpdated.emit({
            platform: 'udaan',
            cartCount: this.apiCartCount,
            cartItems: this.apiCartItems
        });
    }

    /**
     * Internal helper for syncing Udaan cart in real-time
     */
    private updateUdaanCartSync(item: SupplierProduct, qty: number, previousQuantity: number): void {
        const skuId = String(item.sku_id);
        item.isAddingToCart = true;
        this.cartRequestCount += 1;
        const known = this.supplierSection?.items.find((entry) => String(entry.sku_id) === skuId);
        this.priceDiscoveryService.updateUdaanCart(
                    item.sku_id,
                    qty,
                    'Delivery',
                    this.storeUuid,
                    item.category_id || known?.category_id || '',
                    this.entityId,
                    item.vertical || known?.vertical || '',
                    true // skipRedirect: don't open Udaan yet
                ).pipe(takeUntil(this.destroy$)).subscribe({
                    next: (response: any) => {
                        console.log(`✅ Udaan item "${item.item_name}" synced with qty: ${qty}`);
                        console.log('📦 [Udaan] Update cart response:', response);

                        const data = response?.response || response;
                        const newCartCount = data?.cartCount ?? data?.cart_count;

                        this.apiCartCount = newCartCount ?? this.apiCartCount;
                        console.log('📦 [Udaan] Updated cart count:', this.apiCartCount);

                        if (this.apiCartCount === 0) {
                            this.apiCartItems = this.apiCartItems.filter(i => this.qtyDebouncer.isPending(String(i.sku_id)));
                            this.cartQuantityMap.clear();
                            this.apiCartItems.forEach(i => this.cartQuantityMap.set(String(i.sku_id), i.selectedQuantity || 0));
                            if (this.supplierSection) {
                                this.supplierSection.items.forEach(i => {
                                    if (i.platform === 'udaan' && !this.qtyDebouncer.isPending(String(i.sku_id))) {
                                        i.selectedQuantity = 0;
                                    }
                                });
                            }
                        } else if (qty === 0 && !this.qtyDebouncer.isPending(skuId)) {
                            this.apiCartItems = this.apiCartItems.filter(i => String(i.sku_id) !== skuId);
                            this.cartQuantityMap.delete(skuId);
                        }

                        this.cartUpdated.emit({
                            platform: 'udaan',
                            cartCount: this.apiCartCount,
                            cartItems: this.apiCartItems
                        });

                        this.cartRequestCount = Math.max(0, this.cartRequestCount - 1);
                        item.isAddingToCart = false;
                    },
                    error: (err) => {
                        console.error(`❌ Failed to sync Udaan item "${item.item_name}"`, err);
                        this.cartRequestCount = Math.max(0, this.cartRequestCount - 1);
                        item.isAddingToCart = false;
                        if (this.qtyDebouncer.isPending(skuId)) {
                            return;
                        }
                        item.selectedQuantity = previousQuantity;
                        this.syncCartLocally(item);
                        this.sharedService.errorAlert(`Failed to sync "${item.item_name}" with Udaan`);
                    }
                });
    }

    /**
     * Proceed-to-buy flow: Already synced, just redirect a and clear local state.
     */
    proceedToBuy(checkoutItems: SupplierProduct[]): void {
        this.flushPendingQuantitySyncs();
        if (!checkoutItems || checkoutItems.length === 0) return;

        // Since items are already being synced in real-time, just clear local state and redirect
        this.sharedService.successAlert('Redirecting to Udaan...');

        // Clear Udaan cart data from the supplier section
        if (this.supplierSection) {
            this.supplierSection.items.forEach(item => {
                item.selectedQuantity = 0;
            });
        }

        window.open('https://udaan.com/boltCart/?utm_source=petpooja', '_blank');
        this.checkoutComplete.emit();
    }
}
